import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Building2 } from "lucide-react";

const empty = { name: "", slug: "", industry: "", accent_hex: "#0047FF", tagline: "", logo_letter: "", plan: "starter", subscriptionStatus: "free", currentPeriodEnd: "" };

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.slug);
    setForm({
      ...empty, ...c,
      plan: c.plan || 'starter',
      subscriptionStatus: c.subscriptionStatus || c.subscription_status || 'free',
      currentPeriodEnd: c.currentPeriodEnd ? new Date(c.currentPeriodEnd).toISOString().split('T')[0] : '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nom requis");
    try {
      if (editing) {
        await api.put(`/admin/companies/${editing}`, form);
        toast.success("Société mise à jour");
      } else {
        await api.post("/admin/companies", form);
        toast.success("Société créée");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Supprimer la société ${slug} ?`)) return;
    await api.delete(`/admin/companies/${slug}`);
    toast.success("Société supprimée");
    load();
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Sociétés</h1>
        </div>
        <Button onClick={openNew} data-testid="add-company-btn" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold h-11">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle société
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map((c) => (
          <div key={c.slug} data-testid={`company-${c.slug}`} className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-sm flex items-center justify-center overflow-hidden p-1.5" style={{ backgroundColor: c.logo_file_id ? "#fff" : `${c.accent_hex}1a`, border: c.logo_file_id ? "1px solid hsl(var(--border))" : "none" }}>
                {c.logo_file_id ? <img src={fileUrl(c.logo_file_id)} alt="" className="w-full h-full object-contain" />
                  : <span className="font-heading font-bold text-lg" style={{ color: c.accent_hex }}>{(c.logo_letter || c.name[0]).toUpperCase()}</span>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} data-testid={`edit-${c.slug}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(c.slug)} data-testid={`delete-company-${c.slug}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="font-heading font-semibold mt-3">{c.name}</p>
            <p className="text-sm text-muted-foreground">{c.industry || "—"}</p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">/{c.slug}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold border bg-muted text-muted-foreground border-transparent capitalize">
                {c.plan || 'starter'}
              </span>
              <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold border ${
                c.subscriptionStatus === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                c.subscriptionStatus === 'trialing' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                c.subscriptionStatus === 'past_due' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                c.subscriptionStatus === 'canceled' ? 'bg-red-50 text-red-700 border-red-200' :
                'bg-muted text-muted-foreground border-transparent'
              }`}>
                {c.subscriptionStatus === 'active' ? 'Actif' :
                 c.subscriptionStatus === 'trialing' ? 'Essai' :
                 c.subscriptionStatus === 'past_due' ? 'Retard' :
                 c.subscriptionStatus === 'canceled' ? 'Annulé' : 'Gratuit'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="w-4 h-4 rounded-sm border border-border" style={{ backgroundColor: c.accent_hex }} />
              <span className="text-xs text-muted-foreground font-mono">{c.accent_hex}</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier la société" : "Nouvelle société"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nom *</Label><Input value={form.name} onChange={upd("name")} data-testid="company-name-input" className="mt-1.5 rounded-sm" /></div>
            {!editing && <div><Label>Slug (optionnel)</Label><Input value={form.slug} onChange={upd("slug")} placeholder="auto" className="mt-1.5 rounded-sm font-mono" /></div>}
            <div><Label>Secteur</Label><Input value={form.industry} onChange={upd("industry")} className="mt-1.5 rounded-sm" /></div>
            <div><Label>Accroche</Label><Input value={form.tagline} onChange={upd("tagline")} className="mt-1.5 rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plan</Label>
                <select value={form.plan || 'starter'} onChange={upd("plan")}
                  className="mt-1.5 w-full border border-border rounded-sm px-3 py-2 text-sm bg-background">
                  <option value="starter">Starter (gratuit)</option>
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <Label>Statut abonnement</Label>
                <select value={form.subscriptionStatus || 'free'} onChange={upd("subscriptionStatus")}
                  className="mt-1.5 w-full border border-border rounded-sm px-3 py-2 text-sm bg-background">
                  <option value="free">Gratuit</option>
                  <option value="trialing">Essai</option>
                  <option value="active">Actif</option>
                  <option value="past_due">Paiement en retard</option>
                  <option value="canceled">Annulé</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Fin de période (optionnel)</Label>
              <Input type="date" value={form.currentPeriodEnd || ''} onChange={upd("currentPeriodEnd")} className="mt-1.5 rounded-sm" />
            </div>
            <div className="flex items-center gap-3">
              <div><Label>Accent</Label><input type="color" value={form.accent_hex} onChange={upd("accent_hex")} className="mt-1.5 w-12 h-10 rounded-sm border border-border block cursor-pointer" /></div>
              <div className="flex-1"><Label>Initiale logo</Label><Input value={form.logo_letter} onChange={upd("logo_letter")} maxLength={2} className="mt-1.5 rounded-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="save-company-btn" className="rounded-sm bg-primary hover:bg-primary/90">{editing ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
